import { FC, useMemo, ReactElement, useState, useEffect, useCallback } from 'react'

import { CryptoRewardsPanePreset } from 'components/features/rewards/rewardsPane/components/CryptoRewardsPanePreset/CryptoRewardsPanePreset'
import { formatUSCurrency } from 'helpers/formatHelpers'
import {
  CRYPTO_REWARDS_AUTO_INVESTMENTS_MODE_STEP_CHOOSE_CURRENCY,
  CRYPTO_REWARDS_AUTO_INVESTMENTS_MODE_STEP_ENABLE,
} from 'components/features/rewards/rewardsPane/constants/paneSteps'
import { useGetCryptoCurrencyNameById } from 'components/features/rewards/rewardsPane/hooks/useGetCryptoCurrencyNameById'
import { useCryptoRewardsPaneModifyState } from 'components/features/rewards/rewardsPane/hooks/useCryptoRewardsPaneModifyFieldsState'
import { FLOATING_PANE_AUTO_INVESTMENTS_MODE } from 'components/features/rewards/rewardsPane/constants/paneState'
import { CryptoRewardsAutoInvestmentsModeSteps } from 'components/features/rewards/rewardsPane/types/paneStateAutoInvestmentsMode'
import {
  getCryptoCurrencyIdChosenForAutoinvestments,
  getCryptoRewardsPaneProgressPercentsStateSelector,
} from 'components/features/rewards/rewardsPane/store/selectors/paneState'
import { useStoreDispatch, useStoreSelector } from 'helpers/reactReduxHelpers'
import { useSimpleGuard } from 'helpers/loadableHelpers'
import { cryptoMetaGuard } from 'components/features/rewards/rewardsPane/guards/cryptoMetaGuard'
import { rewardsSummaryGuard } from 'components/features/rewards/rewardsPane/guards/rewardsSummaryGuard'
import { CryptoCurrencyId } from 'components/features/rewards/rewardsPane/types/cryptoCurrency'
import { GeneralErrors } from 'interfaces/errors'
import { OrderFailedResult } from 'components/features/rewards/rewardsPane/types/orderResult'
import { getChangeAutoInvestmentsModeFailedResultSelector } from 'components/features/rewards/rewardsPane/store/selectors/orderState'
import { OrderResultsReducerResetLastOrderErrorsAction } from 'components/features/rewards/rewardsPane/store/actions/ordersResults'
import { SuccessCryptoRewardsAutoInvestmentsModeStepAction } from 'components/features/rewards/rewardsPane/store/actions/paneState'
import { CryptoRewardsPaneLinkButton } from 'components/features/rewards/rewardsPane/components/CryptoRewardsPaneLinkButton/CryptoRewardsPaneLinkButton'

const STEP_TITLE = 'Invest Your Rewards Into Crypto'

const STEP_SUBTITLE = 'Turn on auto-investing?'

type CryptoRewardsAutoInvestmentsEnableStepProps = {
  onClose: () => void
}

export const CryptoRewardsAutoInvestmentsEnableStepReady: FC<CryptoRewardsAutoInvestmentsEnableStepProps> = ({ onClose }) => {
  const dispatch = useStoreDispatch()
  const [formGeneralErrorsOrNull, setFormGeneralErrorsOrNull] = useState<GeneralErrors | null>(null)
  const formGeneralErrorToRender: [string] | null = useMemo(() => formGeneralErrorsOrNull ? [formGeneralErrorsOrNull[0]] : null, [formGeneralErrorsOrNull])

  const paneProgressPercent = useStoreSelector(getCryptoRewardsPaneProgressPercentsStateSelector)
  const cryptoCurrencyIDChosenForInvestment: CryptoCurrencyId = useStoreSelector(getCryptoCurrencyIdChosenForAutoinvestments)
  const enableAutoinvestmentsModeErrorsOrNull: OrderFailedResult<typeof FLOATING_PANE_AUTO_INVESTMENTS_MODE> | null = useStoreSelector(getChangeAutoInvestmentsModeFailedResultSelector)

  const cryptoCurrencyName: string = useGetCryptoCurrencyNameById(cryptoCurrencyIDChosenForInvestment)

  const mainText = useMemo<ReactElement>(() => (
    <p>
      When you turn on auto-investing, we’ll automatically invest your rewards in { cryptoCurrencyName } when your cash rewards balance reaches { formatUSCurrency(10, 'penny') } or more. All new rewards you earn will also be automatically invested at the then current market rate. You can turn off auto-investing at any time. There are no fees to purchase crypto and you can sell to convert your rewards back to cash at any time.
    </p>
  ), [cryptoCurrencyName])

  const handleOpenCurrencyPickerStep = useCryptoRewardsPaneModifyState(FLOATING_PANE_AUTO_INVESTMENTS_MODE, {
    step: CRYPTO_REWARDS_AUTO_INVESTMENTS_MODE_STEP_CHOOSE_CURRENCY,
  })
  const openCryptoCurrencyPickerBtn = useMemo<ReactElement>(() => (
    <CryptoRewardsPaneLinkButton onClick={ handleOpenCurrencyPickerStep }>
      Pick another crypto
    </CryptoRewardsPaneLinkButton>
  ), [handleOpenCurrencyPickerStep])

  const updatedPaneFieldsState = useMemo<Required<CryptoRewardsAutoInvestmentsModeSteps['fields']>>(() => ({
    cryptoCurrencyIDChosenForInvestment,
  }), [cryptoCurrencyIDChosenForInvestment])
  const handleSendRequestToChangeAutoModeState = useCallback(() => {
    const paneUpdatedState = {
      step: CRYPTO_REWARDS_AUTO_INVESTMENTS_MODE_STEP_ENABLE,
      fields: updatedPaneFieldsState,
    }
    dispatch(new SuccessCryptoRewardsAutoInvestmentsModeStepAction(paneUpdatedState))
  }, [updatedPaneFieldsState, dispatch])

  const handleClearGeneralErrors = useCallback(() => {
    setFormGeneralErrorsOrNull(null)
  }, [])

  useEffect(() => {
    if (enableAutoinvestmentsModeErrorsOrNull) {
      const { generalErrors, fieldsErrors } = enableAutoinvestmentsModeErrorsOrNull
      if (generalErrors?.length) {
        setFormGeneralErrorsOrNull(generalErrors)
      } else if (fieldsErrors?.autoinvest_ticker) {
        setFormGeneralErrorsOrNull(fieldsErrors.autoinvest_ticker)
      } else if (fieldsErrors?.is_autoinvest_on) {
        setFormGeneralErrorsOrNull(fieldsErrors.is_autoinvest_on)
      }
      dispatch(new OrderResultsReducerResetLastOrderErrorsAction())
    }
  }, [enableAutoinvestmentsModeErrorsOrNull, dispatch])

  return (
    <CryptoRewardsPanePreset
      title={ STEP_TITLE }
      subTitle={ STEP_SUBTITLE }
      actionName='CONFIRM'
      progressBarPercent={ paneProgressPercent }
      errors={ formGeneralErrorToRender }
      onErrorsOverlayClosed={ handleClearGeneralErrors }
      onSuccess={ handleSendRequestToChangeAutoModeState }
      onCancel={ onClose }
    >
      { mainText }
      { openCryptoCurrencyPickerBtn }
    </CryptoRewardsPanePreset>
  )
}

export const CryptoRewardsAutoInvestmentsEnableStep: FC<CryptoRewardsAutoInvestmentsEnableStepProps> = ({ onClose }) => {
  const isCryptoCurrenciesMetaLoaded = useSimpleGuard(
    cryptoMetaGuard,
    {},
    'CryptoRewardsAutoInvestmentsEnableStep'
  )
  const isAutoRewardsFeatureStateLoaded = useSimpleGuard(
    rewardsSummaryGuard,
    {},
    'CryptoRewardsAutoInvestmentsEnableStep'
  )
  const isStepRequiredDataLoading = !(isCryptoCurrenciesMetaLoaded && isAutoRewardsFeatureStateLoaded)
  const stepNode = useMemo<ReactElement>(() => {
    if (isStepRequiredDataLoading) {
      return (
        <CryptoRewardsPanePreset
          isLoading
          title={ STEP_TITLE }
          subTitle={ STEP_SUBTITLE }
          onCancel={ onClose }
        >
          <></>
        </CryptoRewardsPanePreset>
      )
    }
    return <CryptoRewardsAutoInvestmentsEnableStepReady onClose={ onClose } />
  }, [isStepRequiredDataLoading, onClose])

  return stepNode
}
